/*
Import the google.maps type declarations into this file to use the google namespace;
This reference directive avoids having to import `google.maps` into every tsconfig.app.json file.
*/
/// <reference types="google.maps" />

import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, Output,
  PLATFORM_ID, Renderer2
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GOOGLE_MAPS_API_KEY } from '../shared/constants';

import PlaceResult = google.maps.places.PlaceResult;

/**
 * This is an in-house built Angular directive for connecting
 * an input element to Google Map's autocomplete API for places (address search).
 * *(This is an alternative to depending on the `agm` &
 * `@angular-material-extensions/google-maps-autocomplete` packages).*
 *
 * **Usage:**
 *
 * Installing this directive on an input element
 * will dynamically load the google maps places API script
 * and attach an autocomplete widget to that input element.
 *
 * When an autocomplete suggestion is selected this directive
 * will emit it through the `onAutoCompleteSelected` event emitter.
 */
@Directive({
  selector: '[googleMapsAutoComplete]',
  standalone: true
})
export class GoogleMapsAutoCompleteDirective implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  googleMapsAPILoader$!: Observable<void>;

  readonly GOOGLE_MAPS_API_SCRIPT_ID: string = 'googleMapsAPIScript';
  readonly GOOGLE_MAPS_API_SCRIPT_LOADED_CALLBACK_NAME: string = 'googleMapsAPIScriptLoaded';

  /* DOM contract for the google places prediction dropdown ("pac" = places autocomplete). */
  private readonly PAC_PREDICTION_SELECTOR = '.pac-container .pac-item';
  private readonly PAC_HIGHLIGHTED_SELECTOR = '.pac-container .pac-item-selected';

  /* Named keyCodes for the synthetic keypresses that drive the widget. `keyCode` is deprecated
     but retained alongside `key` because the google places widget reads it off the event. */
  private readonly ENTER_KEY_CODE = 13;
  private readonly ARROW_DOWN_KEY_CODE = 40;

  /* Guards against re-entrancy when we re-issue the synthetic Enter keypress below. */
  private isAcceptingPrediction = false;

  /* The native input the google places widget is attached to; the target for the synthetic
     keypresses that drive the widget. Set once the widget attaches in ngAfterViewInit. */
  private autoCompleteInput?: HTMLInputElement;

  @Output()
    onAutoCompleteSelected: EventEmitter<PlaceResult> = new EventEmitter<PlaceResult>();

  @Input() autocompleteParams = {
    componentRestrictions: {
      country: 'us'
    },
    types: ['(regions)']
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderService: Renderer2,
    private hostElementRef: ElementRef,
    private hostFormControlRef: NgControl,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) return;
    this.googleMapsAPILoader$ = this.getGoogleMapsAPIScriptLoader$();
  }

  ngAfterViewInit(): void {
    if (isPlatformServer(this.platformId)) return;
    this.googleMapsAPILoader$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => this.attachGoogleMapsAutoCompleteToHostInput()
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getGoogleMapsAPIScriptLoader$(): Observable<void> {
    return new Observable(
      (subscriber) => {
        /* If the google maps API script already exists in the DOM
        then it has already been loaded; we are done. */
        if (this.document.getElementById(this.GOOGLE_MAPS_API_SCRIPT_ID)) {
          subscriber.next();
          subscriber.complete();
          return;
        }

        try {
          /* Build a script element to inject in the DOM which will asynchronously load the google maps API script.
          (See https://developers.google.com/maps/documentation/javascript/overview#Dynamic for reference). */
          const googleMapsAPIScript = this.renderService.createElement('script') as HTMLScriptElement;
          googleMapsAPIScript.type = 'text/javascript';
          googleMapsAPIScript.async = true;
          googleMapsAPIScript.defer = true;
          googleMapsAPIScript.id = this.GOOGLE_MAPS_API_SCRIPT_ID;
          googleMapsAPIScript.src = `https://maps.googleapis.com/maps/api/js?`
              + `key=${GOOGLE_MAPS_API_KEY}`
              + `&libraries=places`
              /* The `v` parameter specifies the google maps API version release cadence to use.
              This was set to quarterly by default in the AGM package, so we keep it that way for consistency.
              (See https://developers.google.com/maps/documentation/javascript/versions for reference). */
              + `&v=quarterly`
              + `&callback=${this.GOOGLE_MAPS_API_SCRIPT_LOADED_CALLBACK_NAME}`;

          /* If the script's `onerror` callback executes then we know an error occurred
          and our script did not load successfully; emit an error on the observable. */
          googleMapsAPIScript.onerror = (err) => subscriber.error(err);

          /* This callback will be executed by the google maps API script once it successfully finishes loading. */
          window[this.GOOGLE_MAPS_API_SCRIPT_LOADED_CALLBACK_NAME] = () => {
            subscriber.next();
            subscriber.complete();
          };

          this.renderService.appendChild(this.document.head, googleMapsAPIScript);
        } catch (err) {
          subscriber.error(err);
        }
      }
    );
  }

  // Recursively get the HtmlInputElement for google places to attach to.
  private getNativeInputElement(el: HTMLElement): HTMLInputElement | undefined {
    if (el instanceof HTMLInputElement) {
      return el;
    }
    if (el?.children?.length > 0) {
      return this.getNativeInputElement(el.children[0] as HTMLElement);
    }
    return undefined;
  }
  private attachGoogleMapsAutoCompleteToHostInput(): void {
    const inputElement = this.getNativeInputElement(this.hostElementRef.nativeElement);
    if (!inputElement) { return }
    this.autoCompleteInput = inputElement;

    const autoComplete = new google.maps.places.Autocomplete(
      inputElement,
      this.autocompleteParams
    );

    autoComplete.addListener('place_changed', () => this.ngZone.run(() => {
      /* force the host form control to refresh its model
        with the new value loaded in the textbox by the autocomplete */
      this.hostFormControlRef.control.setValue(
        (this.hostElementRef.nativeElement as HTMLInputElement).value
      );

      const place: PlaceResult = autoComplete.getPlace();
      this.onAutoCompleteSelected.emit(place);
    }));
  }

  /**
   * When the prediction dropdown is open and the user presses Enter, accept the highlighted
   * prediction (or the first one, if none is highlighted yet) instead of submitting the raw text.
   * Without this the surrounding form submits/blurs before the place resolves, so the location is
   * never selected and its geometry never loads (MER-4280).
   */
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.autoCompleteInput) return;

    /* Ignore the synthetic keypresses we dispatch below to drive the widget. */
    if (this.isAcceptingPrediction) return;
    if (event.key !== 'Enter' && event.keyCode !== this.ENTER_KEY_CODE) return;

    /* Only intervene while the dropdown is actually open with predictions. */
    if (this.document.querySelectorAll(this.PAC_PREDICTION_SELECTOR).length === 0) return;

    /* Stop the raw text from submitting/blurring the form before the place resolves. */
    event.preventDefault();

    this.isAcceptingPrediction = true;
    try {
      /* If nothing is highlighted yet, highlight the first prediction. */
      if (!this.document.querySelector(this.PAC_HIGHLIGHTED_SELECTOR)) {
        google.maps.event.trigger(
          this.autoCompleteInput, 'keydown', { keyCode: this.ARROW_DOWN_KEY_CODE, key: 'ArrowDown' }
        );
      }
      /* Re-issue Enter so the widget resolves the highlighted prediction (fires place_changed). */
      google.maps.event.trigger(this.autoCompleteInput, 'keydown', { keyCode: this.ENTER_KEY_CODE, key: 'Enter' });
    } finally {
      this.isAcceptingPrediction = false;
    }
  }
}
